import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import { useModal } from '../components/useModal';

/**
 * Non-partner facility — a venue we know about but do not sell.
 *
 * These come from imports and crawls, and the page exists to decide what to do
 * with each one: leave it, blacklist it so it stops being imported, or delete it
 * from the list. Which of the four actions apply depends on the state it is in,
 * and the old page gets that right — blacklisted shows only "remove from
 * blacklist", deleted shows only "restore". Kept exactly.
 *
 * The two `confirm()` calls become dialogs that say what the state change means:
 * a blacklist stops the venue coming back on the next import, which is the whole
 * point and is not obvious from the word.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const NonPartnerFacilityInternal = ({ api }) => {
  const { nonPartnerFacilityId } = useParams();
  const { ask, modalProps } = useModal();

  const [facility, setFacility] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.nonPartnerFacilities.getNonPartnerFacility(nonPartnerFacilityId);
      if (!data || data.success === false || !data.id) {
        setFailed(true);
        return;
      }
      setFacility(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, nonPartnerFacilityId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/non-partner-facilities">← All non-partner facilities</BackToList>
        <div className="next-note next-note--error">
          Could not load this facility.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!facility) return <div className="next-page next-muted">Loading…</div>;

  const run = (fn, okText, failText) => async () => {
    setBusy(true);
    try {
      await fn();
      setNote({ tone: 'info', text: okText });
      await load();
    } catch (error) {
      // 409s carry the reason (slug conflict, too close to another facility, …).
      const reason = error?.payload?.translatedMessage;
      setNote({ tone: 'error', text: reason ? `${failText}: ${reason}` : failText });
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const blacklist = () => ask({
    kind: 'danger',
    title: `Blacklist ${facility.name || 'this facility'}?`,
    context: `Blacklist · ${facility.slug || facility.id}`,
    body: 'It stops being imported: the next crawl or import will not bring it back, and it disappears from the non-partner list.',
    confirmLabel: 'Add to blacklist',
    onConfirm: run(
      () => api.nonPartnerFacilities.addToBlacklist(facility.id),
      'Blacklisted.',
      'Unable to blacklist it.'
    )
  });

  const unblacklist = () => ask({
    kind: 'confirm',
    title: `Remove ${facility.name || 'this facility'} from the blacklist?`,
    context: `Un-blacklist · ${facility.slug || facility.id}`,
    body: 'Imports can bring it back from the next run onwards.',
    confirmLabel: 'Remove from blacklist',
    onConfirm: run(
      () => api.nonPartnerFacilities.removeFromBlacklist(facility.blacklistId),
      'Removed from the blacklist.',
      'Unable to remove it from the blacklist.'
    )
  });

  const softDelete = () => ask({
    kind: 'danger',
    title: `Delete ${facility.name || 'this facility'}?`,
    context: `Delete · ${facility.slug || facility.id}`,
    body: 'It is hidden from the list rather than erased, and can be restored from this page afterwards.',
    confirmLabel: 'Delete',
    onConfirm: run(
      () => api.nonPartnerFacilities.softDelete(facility.id),
      'Deleted.',
      'Unable to delete it.'
    )
  });

  const restore = () => ask({
    kind: 'confirm',
    title: `Restore ${facility.name || 'this facility'}?`,
    context: `Restore · ${facility.slug || facility.id}`,
    body: 'It comes back into the non-partner list.',
    confirmLabel: 'Restore',
    onConfirm: run(
      () => api.nonPartnerFacilities.restore(facility.id),
      'Restored.',
      'Unable to restore it.'
    )
  });

  // The old page's own logic: the state decides which actions exist at all.
  const actions = facility.isBlacklisted
    ? [['Remove from blacklist', unblacklist, null]]
    : facility.isDeleted
      ? [['Restore', restore, null]]
      : [['Add to blacklist', blacklist, 'danger'], ['Delete', softDelete, 'danger']];

  const facts = [
    ['Name', facility.name || <span className="next-muted">—</span>],
    ['Slug', facility.slug || <span className="next-muted">—</span>],
    ['Area', facility.area || <span className="next-muted">—</span>],
    ['Area slug', facility.areaSlug || <span className="next-muted">—</span>],
    ['Source type', facility.sourceType || <span className="next-muted">—</span>],
    ['External id', facility.externalId || <span className="next-muted">—</span>],
    ['Country code', facility.countryCode || <span className="next-muted">—</span>],
    ['Kommune', facility.kommune || <span className="next-muted">—</span>],
    ['Region', facility.region || <span className="next-muted">—</span>],
    ['Address', facility.address || <span className="next-muted">—</span>],
    ['Website', facility.website
      ? <a className="next-link" href={facility.website} target="_blank" rel="noopener noreferrer">{facility.website}</a>
      : <span className="next-muted">—</span>],
    ['Phone', facility.contactPhone || <span className="next-muted">—</span>],
    ['Email', facility.contactEmail || <span className="next-muted">—</span>],
    ['Language', facility.language || <span className="next-muted">—</span>],
    ['Owner type', facility.ownerType || <span className="next-muted">—</span>],
    ['Operator type', facility.operatorType || <span className="next-muted">—</span>],
    ['Latitude', facility.latitude ?? <span className="next-muted">—</span>],
    ['Longitude', facility.longitude ?? <span className="next-muted">—</span>],
    ['Created at', fmtDate(facility.createdAtUtc)],
    ['Updated at', fmtDate(facility.updatedAtUtc)]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/non-partner-facilities">← All non-partner facilities</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{facility.name || 'Non-partner facility'}</h1>
          {facility.isBlacklisted && <span className="next-pill next-pill--bad">Blacklisted</span>}
          {facility.isDeleted && <span className="next-pill next-pill--off">Deleted</span>}
        </div>
        <span className="next-detail__id">{facility.id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* The reason is only recorded when it is blacklisted, and it is the one
          thing somebody looking at a blacklisted venue wants to read. */}
      {facility.isBlacklisted && facility.blacklistReason && (
        <div className="next-note next-note--warn">
          <strong>Blacklisted:</strong> {facility.blacklistReason}
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Facility</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              {actions.map(([label, onClick, tone]) => (
                <button
                  type="button"
                  key={label}
                  className={`next-actionlist__item${tone === 'danger' ? ' next-actionlist__item--danger' : ''}`}
                  disabled={busy}
                  onClick={onClick}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                facility.areaSlug && {
                  label: 'Others in this area',
                  hint: facility.area || facility.areaSlug,
                  to: `/next/non-partner-facilities?areaSlug=${encodeURIComponent(facility.areaSlug)}`
                },
                // Same slug on our side would mean this venue is already a
                // partner under another record.
                facility.slug && {
                  label: 'Partner facilities',
                  hint: 'search by this name',
                  to: `/next/facilities?name=${encodeURIComponent(facility.name || '')}`
                },
                facility.website && { label: 'Their website', href: facility.website }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

NonPartnerFacilityInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const NonPartnerFacility = withApi(NonPartnerFacilityInternal);

NonPartnerFacility.requiresLogin = true;

export default NonPartnerFacility;
